import React from 'react';
import { MoreHorizontal } from 'lucide-react';

export interface MenuPlusItem {
    cle: string;
    icone?: React.ReactNode;
    texte: string;
    action: () => void;
    disabled?: boolean;
    // Action destructrice : affichée en rouge, séparée des autres.
    danger?: boolean;
}

/**
 * Menu « Plus » du suivi de prestation : l'action principale reste un vrai bouton, tout le reste (actions
 * secondaires ou rares) vit ici. Refonte 2026-10-14 (Emeni : « des boutons partout, c'est brouillon »).
 * Clavier : Échap ferme, clic à l'extérieur ferme.
 */
export function MenuPlus({ items, label = 'Plus' }: { items: MenuPlusItem[]; label?: string }) {
    const [ouvert, setOuvert] = React.useState(false);
    const ref = React.useRef<HTMLDivElement>(null);
    React.useEffect(() => {
        if (!ouvert) return;
        const fermer = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOuvert(false); };
        document.addEventListener('mousedown', fermer);
        return () => document.removeEventListener('mousedown', fermer);
    }, [ouvert]);

    const normales = items.filter((i) => !i.danger);
    const dangereuses = items.filter((i) => i.danger);
    const rendu = (it: MenuPlusItem) => (
        <button key={it.cle} type="button" role="menuitem" disabled={it.disabled}
            onClick={() => { setOuvert(false); it.action(); }}
            className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-left disabled:opacity-40 ${it.danger ? 'text-rose-500 hover:bg-rose-500/10' : 'text-content hover:bg-surface-soft'}`}>
            {it.icone} {it.texte}
        </button>
    );

    return (
        <div className="relative" ref={ref}>
            <button type="button" onClick={() => setOuvert((v) => !v)} aria-haspopup="menu" aria-expanded={ouvert}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-bold bg-surface border border-border text-content hover:border-brand-500 hover:bg-brand-600/10 transition-colors">
                <MoreHorizontal size={14} /> {label}
            </button>
            {ouvert && (
                <div role="menu" className="absolute right-0 top-full mt-1.5 z-20 min-w-[210px] rounded-xl border border-border bg-surface shadow-lg p-1.5"
                    onKeyDown={(e) => { if (e.key === 'Escape') setOuvert(false); }}>
                    {normales.map(rendu)}
                    {dangereuses.length > 0 && normales.length > 0 && <div className="my-1 border-t border-border" />}
                    {dangereuses.map(rendu)}
                </div>
            )}
        </div>
    );
}
